
import {
  Component,
  ChangeDetectionStrategy,
  output,
  inject,
  signal,
  computed,
  ElementRef,
  ViewChild,
  HostListener,
  effect
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { InputGroupModule } from 'primeng/inputgroup';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { ContextMenu, ContextMenuModule } from 'primeng/contextmenu';
import { AuthService } from '../../services/auth.service';
import { ApiService } from '../../services/api.service';
import { UserService } from '../../services/user.service';
import { GlobalSearchController } from '../../services/global-search.controller';
import { PlatformShortcutService } from '../../services/platform-shortcut.service';
import { SearchShortcutHintComponent } from '../global-search/search-shortcut-hint.component';
import { ChromeThemeService } from '../../services/chrome-theme.service';

export type { GlobalSearchHit, GlobalSearchScope, GlobalSearchScopeOption } from '../../services/global-search.types';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconModule,
    FormsModule,
    AutoCompleteModule,
    InputGroupModule,
    ButtonModule,
    InputTextModule,
    InputGroupAddonModule,
    ContextMenuModule,
    SearchShortcutHintComponent
  ],
  styles: [
    `
      :host { display: block; }

      :host ::ng-deep .navbar-global-search {
        border: 1px solid var(--chrome-border-strong);
        border-radius: 0.75rem;
        background: var(--chrome-input-bg);
        overflow: visible;
        transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
      }

      :host ::ng-deep .navbar-global-search:focus-within {
        border-color: var(--chrome-focus-border);
        box-shadow: 0 0 0 3px var(--chrome-focus-ring);
      }

      :host ::ng-deep .navbar-global-search .p-inputgroupaddon,
      :host ::ng-deep .navbar-global-search .p-inputgroup-addon {
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
        display: flex;
        align-items: center;
      }

      :host ::ng-deep .navbar-global-search > .p-autocomplete,
      :host ::ng-deep .navbar-global-search .p-autocomplete-input,
      :host ::ng-deep .navbar-global-search input.p-inputtext {
        width: 100%;
      }

      :host ::ng-deep .navbar-global-search .p-autocomplete-input:focus,
      :host ::ng-deep .navbar-global-search input.p-inputtext:focus {
        outline: none;
      }

      :host ::ng-deep .navbar-global-search .p-autocomplete {
        position: relative;
        display: flex;
        align-items: center;
      }

      :host ::ng-deep .navbar-global-search .p-autocomplete-input,
      :host ::ng-deep .navbar-global-search input.p-inputtext {
        background: transparent !important;
        border: none !important;
        box-shadow: none !important;
        color: var(--chrome-text-strong) !important;
        font-size: 0.875rem !important;
        font-weight: 500;
        line-height: 1.25 !important;
        padding-top: 0 !important;
        padding-bottom: 0 !important;
      }

      :host ::ng-deep .navbar-global-search .p-autocomplete-input::placeholder,
      :host ::ng-deep .navbar-global-search input.p-inputtext::placeholder {
        color: var(--chrome-text-muted) !important;
        opacity: 1;
      }

      :host ::ng-deep .navbar-global-search .p-autocomplete-loader {
        position: absolute;
        top: 50%;
        right: 0.5rem;
        z-index: 2;
        width: 1rem;
        height: 1rem;
        transform: translateY(-50%);
        color: var(--chrome-loader);
      }

      :host ::ng-deep .navbar-search-autocomplete-wrap {
        position: relative;
        flex: 1 1 auto;
        min-width: 0;
        display: flex;
        align-items: center;
      }

      :host ::ng-deep .navbar-search-shortcut-hint.search-shortcut-hint--overlay {
        position: absolute;
        inset: 0;
        z-index: 1;
        display: flex;
        align-items: center;
        padding: 0 0.75rem 0 0;
        pointer-events: none;
        overflow: hidden;
        white-space: nowrap;
      }

      :host ::ng-deep .navbar-search-shortcut-hint.search-shortcut-hint--overlay .search-shortcut-hint {
        min-width: 0;
        overflow: hidden;
      }
    `
  ]
})
export class NavbarComponent {
  private authService = inject(AuthService);
  private apiService = inject(ApiService);
  private userService = inject(UserService);
  private router = inject(Router);
  readonly search = inject(GlobalSearchController);
  readonly shortcuts = inject(PlatformShortcutService);
  readonly themeService = inject(ChromeThemeService);

  toggleSidebar = output<void>();
  isAuthenticated = computed(() => this.authService.isAuthenticated());
  userInfo = signal(this.authService.getIdTokenClaims());
  userPicture = computed(
    () => this.authService.profileAvatarUrlSignal() || this.authService.getUserPicture() || '/assets/avatar/avatar1.png'
  );
  userName = computed(() => {
    const info = this.userInfo();
    return (
      info?.name ||
      info?.preferred_username ||
      info?.email ||
      this.authService.getUserDisplayName() ||
      'Usuario'
    );
  });
  userEmail = computed(() => this.userInfo()?.email || this.authService.getIdTokenClaims()?.email || '');
  isUserMenuOpen = signal(false);
  apiEnv = signal<string | null>(null);
  isSandbox = computed(() => this.apiEnv() === 'sandbox');

  showSearchShortcutHint = computed(() => {
    const term = this.search.term();
    if (term == null) {
      return true;
    }
    if (typeof term === 'string') {
      return term.trim().length === 0;
    }
    return false;
  });

  @ViewChild('userMenuRoot') userMenuRoot?: ElementRef<HTMLElement>;
  @ViewChild('globalSearchRoot') globalSearchRoot?: ElementRef<HTMLElement>;
  @ViewChild('searchScopeMenu') searchScopeMenu?: ContextMenu;

  constructor() {
    effect(() => {
      if (this.search.suggestions().length > 0) {
        setTimeout(() => this.syncGlobalSearchPanelLayout());
      }
    });

    if (this.isAuthenticated()) {
      this.authService.getUserInfo().subscribe({
        next: (info) => {
          if (info) {
            this.userInfo.set(info);
          }
        }
      });
      this.apiService.getVersion().subscribe({
        next: (res) => this.apiEnv.set(res.env),
        error: () => {
          /* ignorar errores, no mostrar banner */
        }
      });
      this.userService.getMe().subscribe({
        next: (res) => {
          if (res.ok && res.body?.avatarUrl) {
            this.authService.setProfileAvatarUrl(res.body.avatarUrl);
          }
        },
        error: () => {
          /* avatar opcional */
        }
      });
    }
  }

  syncGlobalSearchPanelLayout(): void {
    const root = this.globalSearchRoot?.nativeElement;
    if (!root) {
      return;
    }

    requestAnimationFrame(() => {
      const overlay = document.querySelector(
        '.navbar-global-search-panel.p-autocomplete-overlay'
      ) as HTMLElement | null;
      if (!overlay) {
        return;
      }

      const rect = root.getBoundingClientRect();
      const width = Math.round(rect.width);
      const left = Math.round(rect.left + window.scrollX);
      const top = Math.round(rect.bottom + window.scrollY);

      overlay.style.width = `${width}px`;
      overlay.style.minWidth = `${width}px`;
      overlay.style.maxWidth = `${width}px`;
      overlay.style.left = `${left}px`;
      overlay.style.top = `${top}px`;
      overlay.style.boxSizing = 'border-box';
    });
  }

  openSearchScopeMenu(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.searchScopeMenu?.toggle(event);
  }

  goToProfile(): void {
    this.isUserMenuOpen.set(false);
    void this.router.navigate(['/profile']);
  }

  logout(): void {
    this.isUserMenuOpen.set(false);
    this.authService.logout();
  }

  toggleUserMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.isUserMenuOpen.update((value) => !value);
  }

  closeUserMenu(): void {
    this.isUserMenuOpen.set(false);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node | null;
    if (!target) {
      return;
    }
    if (this.userMenuRoot?.nativeElement.contains(target)) {
      return;
    }
    this.isUserMenuOpen.set(false);
  }
}
